import { useState, useContext, useMemo, useEffect } from "react";
import { Button } from "@/modern/components/ui/button";
import { BaseModal } from "../../base/BaseModal.jsx";
import { ThumbsUp, ThumbsDown, Sparkles } from "lucide-react";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { assignAITags } from "@/network/network.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { REACT_EDITOR_CONTEXT_MENU_KEYS, VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { toast } from "sonner";
import { useIntegrationErrorReporter } from "@/integrations";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { IncentiveTag } from "@/bryntum-lib/models/IncentiveTag.jsx";
import { IncentiveTagCategory } from "@/bryntum-lib/models/IncentiveTagCategory.jsx";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure.js";

interface PointsAssignmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pointsAssignment: {
    positiveTagIds: number[];
    negativeTagIds: number[];
  };
  onPointsChange: (points: { positiveTagIds: number[]; negativeTagIds: number[] }) => void;
  documentId?: number;
  editorInstance?: unknown;
}

const SpotIncentivePointsAssignment = ({ open, onOpenChange, pointsAssignment, onPointsChange, documentId, editorInstance }: PointsAssignmentProps) => {
  const { project, user, viewManager } = useContext(BryntumContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  const { reportError } = useIntegrationErrorReporter();
  const [localPositiveTagIds, setLocalPositiveTagIds] = useState<number[]>(pointsAssignment.positiveTagIds || []);
  const [localNegativeTagIds, setLocalNegativeTagIds] = useState<number[]>(pointsAssignment.negativeTagIds || []);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Sync local state with parent state when pointsAssignment prop changes
  useEffect(() => {
    setLocalPositiveTagIds(pointsAssignment.positiveTagIds || []);
    setLocalNegativeTagIds(pointsAssignment.negativeTagIds || []);
  }, [pointsAssignment.positiveTagIds, pointsAssignment.negativeTagIds]);

  const { version: incentiveTagsVersion } = useSwivuStore<IncentiveTag>({ storeId: STORE_KEYS.incentiveTagsStore });
  const { version: incentiveTagCategoryVersion } = useSwivuStore<IncentiveTagCategory>({ storeId: STORE_KEYS.incentiveTagCategoryStore });

  const tags = useMemo(() => {
    if (!project?.incentiveTagsStore) return [];

    const allTags = project.incentiveTagsStore.query(() => true, true) || [];

    return allTags.map((tag: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
      id: Number(tag.id), // Ensure ID is a number
      name: tag.name,
      categoryId: tag.categoryId,
      categoryName: tag.categoryRecord.name,
      categoryColor: tag.categoryRecord.color,
      categoryPoints: tag.categoryRecord.points
    }));
  }, [project, incentiveTagsVersion]);

  const categories = useMemo(() => {
    const categoryMap = new Map();

    tags.forEach((tag: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
      const categoryKey = tag.categoryId;
      if (!categoryMap.has(categoryKey)) {
        categoryMap.set(categoryKey, {
          id: categoryKey,
          name: tag.categoryName,
          color: tag.categoryColor,
          points: tag.categoryPoints,
          tags: []
        });
      }
      categoryMap.get(categoryKey).tags.push(tag);
    });

    return Array.from(categoryMap.values());
  }, [tags, incentiveTagCategoryVersion]);

  // Update local state when dialog opens or closes
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setLocalPositiveTagIds(pointsAssignment.positiveTagIds || []);
      setLocalNegativeTagIds(pointsAssignment.negativeTagIds || []);
    } else {
      // Reset local state when closing without saving
      setLocalPositiveTagIds(pointsAssignment.positiveTagIds || []);
      setLocalNegativeTagIds(pointsAssignment.negativeTagIds || []);
    }
    onOpenChange(isOpen);
  };

  const toggleTag = (tagId: number) => {
    const currentPositive = [...localPositiveTagIds];
    const currentNegative = [...localNegativeTagIds];

    // Remove from both arrays first (like PointsStep.jsx does)
    const filteredPositive = currentPositive.filter((id) => id !== tagId);
    const filteredNegative = currentNegative.filter((id) => id !== tagId);

    // Determine next state: neutral -> positive -> negative -> neutral
    const isPositive = localPositiveTagIds.includes(tagId);
    const isNegative = localNegativeTagIds.includes(tagId);

    if (!isPositive && !isNegative) {
      // Currently neutral, make positive
      filteredPositive.push(tagId);
    } else if (isPositive) {
      // Currently positive, make negative
      filteredNegative.push(tagId);
    }
    // If currently negative, both arrays stay empty (neutral)

    setLocalPositiveTagIds(filteredPositive);
    setLocalNegativeTagIds(filteredNegative);
  };

  const getTagState = (tagId: number): 'positive' | 'negative' | 'neutral' => {
    if (localPositiveTagIds.includes(tagId)) return 'positive';
    if (localNegativeTagIds.includes(tagId)) return 'negative';
    return 'neutral';
  };

  const getTotalPoints = (): number => {
    let total = 0;

    // Calculate positive points
    localPositiveTagIds.forEach(tagId => {
      const tag = tags.find((t: any) => t.id === tagId); // eslint-disable-line @typescript-eslint/no-explicit-any
      if (tag) {
        total += tag.categoryPoints;
      }
    });

    // Calculate negative points
    localNegativeTagIds.forEach(tagId => {
      const tag = tags.find((t: any) => t.id === tagId); // eslint-disable-line @typescript-eslint/no-explicit-any
      if (tag) {
        total -= tag.categoryPoints;
      }
    });

    return total;
  };

  const getPositiveCount = (): number => {
    return localPositiveTagIds.length;
  };

  const getNegativeCount = (): number => {
    return localNegativeTagIds.length;
  };

  const runAIPointAdjustment = async () => {
    if (!documentId) {
      toast.error("Please upload an image first to use AI Adjustment");
      return;
    }

    if (tags.length === 0) {
      toast.error("No tags available");
      return;
    }

    setIsAnalyzing(true);

    try {
      // Get board ID from context
      const selectedBoard = getSavedBoardSelection();
      // Call the AI tag assignment API
      const result = await assignAITags({
        board_id: selectedBoard.id,
        document_id: documentId
      });

      // Update local state with AI-assigned tags
      setLocalPositiveTagIds(result.data.positive_tag_ids);
      setLocalNegativeTagIds(result.data.negative_tag_ids);

      toast.success("AI analysis complete");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      reportError({
        error,
        provider: "openai",
        fallback: "Unable to analyze image. Please try again.",
        title: "AI Point Adjustment failed",
      });
      // Keep current values on error
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSave = () => {
    onPointsChange({
      positiveTagIds: localPositiveTagIds,
      negativeTagIds: localNegativeTagIds
    });
    onOpenChange(false);
  };

  const MODAL_SIZE_CONFIG = {
    width: "auto",
    height: "auto",
    minWidth: "min(90vw, 45rem)",
    minHeight: "min(90vh, 40rem)",
    maxWidth: "90vw",
    maxHeight: "90vh"
  };

  if (!open) return null;

  return (
    <BaseModal
      title="Points Adjustment"
      description="Assign positive or negative points to construction tags"
      onClose={() => handleOpenChange(false)}
      size={MODAL_SIZE_CONFIG}
      editorInstance={editorInstance}
      record={{}}
      headerIconCls=""
      modal={false}
      version=""
      swivuAssistanceConfig={null}
      contextMenuConfig={{
        [REACT_EDITOR_CONTEXT_MENU_KEYS.ARCHIVE]: {
          hidden: true
        },
        [REACT_EDITOR_CONTEXT_MENU_KEYS.UNARCHIVE]: {
          hidden: true
        },
        [REACT_EDITOR_CONTEXT_MENU_KEYS.DELETE]: {
          hidden: true
        },
        settings: {
          label: "Settings",
          icon: "fas fa-cog",
          disabled: !user || !user.can(GRANTS.grant_one),
          onClick: () => {
            viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
              setting: SETTING_KEYS.INCENTIVE_CATEGORIES_AND_TAGS
            }});
          }
        }
      }}
      footerRenderer={() => (
        <div className="flex justify-between items-center gap-8 w-full">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">Total Points</span>
              <div className="bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                {getTotalPoints()} points
              </div>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <div className="flex items-center gap-1">
                <ThumbsUp className="h-4 w-4 text-green-600 dark:text-green-500" />
                <span>{getPositiveCount()}</span>
              </div>
              <div className="flex items-center gap-1">
                <ThumbsDown className="h-4 w-4 text-destructive" />
                <span>{getNegativeCount()}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={runAIPointAdjustment}
              disabled={isAnalyzing || !documentId || tags.length === 0}
              className="flex items-center gap-2 text-sm"
            >
              <Sparkles className="h-4 w-4" />
              {isAnalyzing ? "Analyzing..." : "AI Adjustment"}
            </Button>
            <Button variant="outline" onClick={() => handleOpenChange(false)} className="text-sm">
              Cancel
            </Button>
            <Button onClick={handleSave} className="text-sm">
              Done
            </Button>
          </div>
        </div>
      )}
    >
      <div className="space-y-6 p-4 overflow-y-auto bg-background flex-1">
        {categories.length > 0 ? (
          categories.map((category) => (
            <div key={category.id} className="space-y-3">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
                <h3 className="font-medium text-base">
                  {category.name} <span className="text-muted-foreground">({category.points} points each)</span>
                </h3>
              </div>

              <div className="flex flex-wrap gap-2">
                {category.tags.map((tag: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
                  const state = getTagState(tag.id);

                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-colors ${state === 'positive'
                        ? 'bg-green-600/10 dark:bg-green-500/15 text-green-700 dark:text-green-400 border border-green-600/20 dark:border-green-500/20 hover:bg-green-600/15 dark:hover:bg-green-500/20'
                        : state === 'negative'
                          ? 'bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive/15'
                          : 'bg-muted/50 text-foreground border border-border hover:bg-muted'
                        }`}
                    >
                      {state === 'positive' && <ThumbsUp className="h-3 w-3" />}
                      {state === 'negative' && <ThumbsDown className="h-3 w-3" />}
                      <span>{tag.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center p-6 border rounded-lg border-dashed">
            <p className="text-muted-foreground text-sm">
              No tags available. Create tags in Board Settings → Categories & Tags to assign points.
            </p>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default SpotIncentivePointsAssignment;
